import { Link } from 'react-router'

import type { LandingBlockProps } from '@features/landing-page-builder/model/blockRegistry'
import { resolveLandingAction } from '@features/landing-page-builder/model/resolveTarget'
import MainPageLikeBlock from '@features/landing-page-builder/ui/blocks/MainPageLikeBlock'

import { cn } from '@shared/lib/cn'

import styles from './LandingBannerBlock.module.scss'

/*
  Hero card of an event landing, styled entirely from the payload (colors,
  radius, height) so a landing can be restyled without a release. `image` is
  optional by design: with no creative we render a flat colored card carrying the
  title — the designed fallback, not an error state.

  A payload carrying `id` instead is a CMS Banner entity — that is the main
  page's banner block, so it is delegated to the shared renderer.
*/
interface BannerPayload {
  id?: string;
  title?: string;
  subtitle?: string;
  subTitle?: string;
  description?: string;
  image?: string;
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: number;
  minHeight?: number;
  layout?: string;
  actionId?: string;
}

export default function LandingBannerBlock (props: LandingBlockProps) {
  const { block, payload, actions } = props

  const {
    id,
    title,
    subtitle,
    subTitle,
    description,
    image,
    backgroundColor,
    textColor,
    borderRadius,
    minHeight,
    layout,
    actionId,
  } = (payload ?? {}) as BannerPayload

  // CMS banner (main-page contract) — reuse the shared renderer.
  if (id) {
    return <MainPageLikeBlock {...props} />
  }

  const caption = subtitle || subTitle || description

  if (!title && !caption && !image) {
    return null
  }

  const target = resolveLandingAction(actions, actionId, block.target)

  const content = (
    <div
      className={cn(styles.root__card, {
        [styles._large]: layout === 'large-card',
        [styles._image]: Boolean(image),
      })}
      style={{
        backgroundColor: backgroundColor || undefined,
        color: textColor || undefined,
        borderRadius: typeof borderRadius === 'number' ? `${borderRadius}px` : undefined,
        minHeight: typeof minHeight === 'number' ? `${minHeight}px` : undefined,
      }}
    >
      {image && (
        <img className={styles.root__image} src={image} alt={title ?? ''} loading="eager" />
      )}

      <div className={styles.root__content}>
        {title && <h1 className={styles.root__title}>{title}</h1>}
        {caption && <p className={styles.root__subtitle}>{caption}</p>}
      </div>
    </div>
  )

  if (!target) {
    return <section className={styles.root}>{content}</section>
  }

  return (
    <section className={styles.root}>
      {target.external ? (
        <a href={target.href} target="_blank" rel="noreferrer noopener">
          {content}
        </a>
      ) : (
        <Link to={target.href}>{content}</Link>
      )}
    </section>
  )
}
